import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Search, ArrowRight, Loader2 } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { VehicleCard, VehicleCardSkeleton } from '@/components/VehicleCard';
import { FilterSidebar } from '@/components/FilterSidebar';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80";

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize, setPageSize] = useState(6);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 6,
    total: 0,
    total_pages: 1
  });

  // Parse filters from URL
  const getFiltersFromURL = useCallback(() => {
    const filters = {};
    for (const [key, value] of searchParams.entries()) {
      if (key === 'page') {
        filters.page = parseInt(value);
      } else if (['precio_min', 'precio_max', 'modelo_min', 'modelo_max'].includes(key)) {
        filters[key] = parseFloat(value);
      } else if (key === 'destacado') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value;
      }
    }
    return filters;
  }, [searchParams]);

  const [filters, setFilters] = useState(getFiltersFromURL);

  // Sync filters when URL params change (e.g. clicking "Inicio" clears params)
  const location = useLocation();
  useEffect(() => {
    setFilters(getFiltersFromURL());
    // Scroll to vehicles section when coming from search (#vehiculos)
    if (location.hash === '#vehiculos') {
      setTimeout(() => {
        document.getElementById('vehicles-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams, getFiltersFromURL, location.hash]);

  // Fetch filter options and settings
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [filtersData, settingsData] = await Promise.all([
          api.getFilters().catch(() => null),
          api.getSettings().catch(() => ({ vehicles_per_page: 6 }))
        ]);
        if (filtersData) setFilterOptions(filtersData);
        if (settingsData?.vehicles_per_page) setPageSize(settingsData.vehicles_per_page);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch vehicles (initial load or filter change)
  const fetchVehicles = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setVehicles([]);
    }
    
    try {
      const data = await api.getVehicles({
        page: 1,
        page_size: pageSize,
        status: 'publicado',
        ...filters
      });
      
      setVehicles(data.vehicles);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error al cargar los vehiculos');
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  // Load more vehicles
  const loadMoreVehicles = useCallback(async () => {
    if (loadingMore || vehicles.length >= pagination.total) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const data = await api.getVehicles({
        page: nextPage,
        page_size: pageSize,
        status: 'publicado',
        ...filters
      });

      setVehicles(prev => [...prev, ...data.vehicles]);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error loading more vehicles:', error);
      toast.error('Error al cargar más vehículos');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, vehicles.length, pagination, pageSize, filters]);

  useEffect(() => {
    fetchVehicles(true);
  }, [fetchVehicles]);

  // Update URL when filters change
  const handleFilterChange = (newFilters) => {
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'page') {
        params.set(key, value.toString());
      }
    });
    setSearchParams(params);
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setFilters({});
  };

  const hasMoreVehicles = vehicles.length < pagination.total;

  // Infinite scroll: load one batch when sentinel enters viewport
  const sentinelRef = useRef(null);
  const loadTriggered = useRef(false);

  useEffect(() => {
    // Reset trigger when new data arrives (loadingMore goes false)
    if (!loadingMore) loadTriggered.current = false;
  }, [loadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMore && hasMoreVehicles && !loadTriggered.current) {
          loadTriggered.current = true;
          loadMoreVehicles();
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadingMore, hasMoreVehicles, loadMoreVehicles]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="hero-overlay" />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 pb-16 md:pb-24">
          <div className="max-w-2xl animate-slide-up">
            <p className="text-primary text-sm font-bold uppercase tracking-widest mb-4">
              Vehículos de Salvamento en GDL
            </p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white mb-6">
              Autos Chocados<br />
              <span className="text-primary">GDL</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-lg">
              Tu mejor opción en vehículos de salvamento. Autos, camionetas, tractocamiones y más. 
              Imágenes de alta resolución para ver cada detalle.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                className="btn-primary"
                onClick={() => document.getElementById('vehicles-section')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta"
              >
                Ver Inventario
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles Section */}
      <section id="vehicles-section" className="py-16 md:py-24" data-testid="vehicles-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-primary text-sm font-bold uppercase tracking-widest mb-2">
                Nuestro Inventario
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-foreground">
                Vehículos Disponibles
              </h2>
            </div>
            <p className="text-muted-foreground">
              Mostrando <span className="text-primary font-bold">{vehicles.length}</span> de{' '}
              <span className="text-primary font-bold">{pagination.total}</span> vehículos
            </p>
          </div>

          {/* Content Layout */}
          <div className="flex gap-8">
            {/* Filters */}
            <FilterSidebar
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              vehicleCount={pagination.total}
            />

            {/* Vehicle Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(pageSize)].map((_, i) => (
                    <VehicleCardSkeleton key={i} />
                  ))}
                </div>
              ) : vehicles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
                    {vehicles.map((vehicle, index) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
                    ))}
                  </div>

                  {/* Infinite scroll sentinel */}
                  {hasMoreVehicles && (
                    <div ref={sentinelRef} className="flex justify-center items-center mt-12 h-16">
                      {loadingMore && <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16" data-testid="no-results">
                  <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    No se encontraron vehículos
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Intenta ajustar los filtros de búsqueda
                  </p>
                  <Button onClick={handleClearFilters} className="bg-primary hover:bg-primary/90 text-white font-bold px-6 py-2">
                    Limpiar Filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
